import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown, ArrowRight, Minus, AlertTriangle,
} from "lucide-react";
import { fetchDxyWrap, fetchDxyWrapArchive } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import DxyPriceTicker, { canonicalPair } from "@/components/cot/DxyPriceTicker";
import DxyCloseHistory from "@/components/cot/DxyCloseHistory";
import { forexOpen } from "@/lib/marketHours";

// Split a long block into readable paragraphs. Non-lossy: only splits at real
// sentence boundaries (". " before a capital/paren), never inside decimals like 1.3439.
const splitParas = (text) => {
  const t = String(text || "").trim();
  if (!t) return [];
  if (/\n/.test(t)) return t.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  const sentences = t.split(/(?<=[.!?])\s+(?=[A-Z($"'])/).map((s) => s.trim()).filter(Boolean);
  const out = [];
  for (let i = 0; i < sentences.length; i += 2) out.push(sentences.slice(i, i + 2).join(" "));
  return out;
};

const Prose = ({ text, cls = "text-[13px] text-zinc-300 leading-relaxed" }) => (
  <div className="space-y-3">
    {splitParas(text).map((p, i) => (
      <p key={i} className={cls}>{p}</p>
    ))}
  </div>
);

const BIAS = {
  bearish: { label: "USD Bearish", short: "Bearish", cls: "text-neg", Icon: TrendingDown },
  bullish: { label: "USD Bullish", short: "Bullish", cls: "text-pos", Icon: TrendingUp },
  neutral: { label: "USD Neutral", short: "Neutral", cls: "text-zinc-500", Icon: Minus },
};
const SIGNAL = {
  up: { cls: "text-pos", arrow: "▲" },
  down: { cls: "text-neg", arrow: "▼" },
  flat: { cls: "text-zinc-500", arrow: "▬" },
};

function ZoneHeader({ n, label, hint }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[11px] font-mono text-amber-term/60 tabular-nums">{n}</span>
      <h2 className="text-[11px] uppercase tracking-[0.28em] text-zinc-200 font-mono whitespace-nowrap">{label}</h2>
      {hint && <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-zinc-600 truncate">{hint}</span>}
      <span className="flex-1 border-b border-zinc-800/70 ml-1 translate-y-[-3px]" />
    </div>
  );
}

function Card({ n, label, hint, children, testid }) {
  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid={testid}>
      <div className="flex items-baseline gap-3 px-4 py-2.5 border-b border-zinc-900">
        <span className="text-[11px] font-mono text-amber-term/60 tabular-nums">{n}</span>
        <span className="text-[11px] uppercase tracking-[0.28em] text-zinc-200 font-mono whitespace-nowrap">{label}</span>
        {hint && <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-zinc-600 truncate">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export default function DxySessionPage() {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [date, setDate] = useState(null);

  const q = useQuery({
    queryKey: ["dxy-wrap", date],
    queryFn: () => fetchDxyWrap(false, date),
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
  const archive = useQuery({ queryKey: ["dxy-archive"], queryFn: fetchDxyWrapArchive, staleTime: 1000 * 60 * 10 });
  const d = q.data;
  const bias = BIAS[d?.bias] || BIAS.neutral;

  const refresh = async () => {
    setRefreshing(true);
    const before = d?.generated_at;
    try {
      await fetchDxyWrap(true).catch(() => null);
      for (let i = 0; i < 16; i++) {
        const latest = await fetchDxyWrap(false).catch(() => null);
        if (latest?.generated_at && latest.generated_at !== before) {
          setDate(null);
          qc.setQueryData(["dxy-wrap", null], latest);
          qc.invalidateQueries({ queryKey: ["dxy-archive"] });
          break;
        }
        await new Promise((r) => setTimeout(r, 10000));
      }
    } finally {
      setRefreshing(false);
    }
  };
  const busy = q.isFetching || refreshing;
  const genTime = d?.generated_at ? new Date(d.generated_at * 1000).toISOString().replace("T", " ").slice(0, 16) + " UTC" : null;

  // Weekend / closed-market notice — updates every minute.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const marketClosed = !forexOpen(new Date(nowTs));
  const lastSessionDay = d?.session_key
    ? new Date(`${d.session_key}T00:00:00Z`).toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" })
    : null;
  const wraps = archive.data?.wraps || [];

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="dxy-session-page">
      {/* Sticky terminal header + live ticker (pinned together) */}
      <div className="sticky top-0 z-20 bg-black">
      <div className="border-b border-zinc-800 px-4 py-2.5 flex items-center gap-3 bg-black">
        <Link to="/" data-testid="dxy-back" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-amber-term transition-colors">
          <ArrowLeft size={13} strokeWidth={1.5} /> Terminal
        </Link>
        <span className="text-zinc-700">/</span>
        <span className="text-[13px] font-semibold text-zinc-100">DXY Session Wrap</span>
        <div className="ml-auto flex items-center gap-3">
          {wraps.length > 0 && (
            <Select value={date || d?.session_key || ""} onValueChange={(v) => setDate(v)}>
              <SelectTrigger data-testid="dxy-archive-select" className="h-7 w-[150px] bg-[#09090B] border-zinc-800 text-[11px] font-mono uppercase tracking-widest text-zinc-300">
                <span className="truncate tabular-nums">{date || d?.session_key || "Archive"}</span>
              </SelectTrigger>
              <SelectContent className="dxy-archive-content bg-[#09090B] border-zinc-800 max-h-80 w-[300px]">
                {wraps.map((w) => {
                  const wb = BIAS[w.bias] || BIAS.neutral;
                  return (
                    <SelectItem key={w.session_key} value={w.session_key} className="text-zinc-300" data-testid={`dxy-archive-${w.session_key}`}>
                      <div className="flex flex-col gap-0.5 pr-2 py-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] text-zinc-400 tabular-nums">{w.session_key}</span>
                          <span className={`text-[10px] uppercase tracking-wider font-mono ${wb.cls}`}>{wb.short}</span>
                        </div>
                        {w.title && <span className="text-[11px] text-zinc-300 truncate max-w-[250px]">{w.title}</span>}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
          <button onClick={refresh} disabled={busy} data-testid="dxy-refresh" title="Regenerate latest" className="text-zinc-500 hover:text-amber-term transition-colors disabled:opacity-50">
            <RefreshCw size={13} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <DxyPriceTicker />
      </div>

      <main className="max-w-5xl mx-auto px-3 lg:px-6 py-6">
        {q.isLoading ? (
          <div className="h-[60vh] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">Loading dollar session read…</div>
        ) : q.isError && !d ? (
          <div className="border border-zinc-800 bg-[#09090B] p-8 text-center">
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-4">No session wrap generated yet</p>
            <button onClick={refresh} disabled={busy} data-testid="dxy-generate" className="text-[11px] uppercase tracking-widest text-amber-term border border-amber-500/50 hover:bg-amber-500/10 px-4 py-2 transition-colors disabled:opacity-50">
              {busy ? "Generating…" : "Generate now"}
            </button>
          </div>
        ) : (
          <div className="space-y-12">
            {/* ══ HERO ══ */}
            <section data-testid="dxy-hero">
              <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-zinc-500">
                <span>Session {d.session_key}</span>
                {date && <span className="text-amber-term">· Archive</span>}
              </div>
              {marketClosed && (
                <div
                  className="mb-3 inline-flex items-center gap-2 border border-zinc-800 bg-[#09090B] px-3 py-1.5 text-[11px] uppercase tracking-widest text-zinc-400"
                  data-testid="dxy-market-closed-banner"
                >
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-neg shrink-0" />
                  Market closed{lastSessionDay ? ` — showing ${lastSessionDay}'s close` : ""}
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight text-zinc-100">{d.title}</h1>
                  <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">{d.subtitle}</p>
                </div>
                <div className={`shrink-0 border border-current ${bias.cls} px-3 py-1.5 flex items-center gap-1.5 font-mono uppercase tracking-widest text-[11px]`} data-testid="dxy-bias">
                  <bias.Icon size={13} strokeWidth={2} /> {bias.label}
                </div>
              </div>
              {genTime && <p className="text-[11px] uppercase tracking-widest text-zinc-600 mt-2">Generated {genTime}{d.stale ? " · last available" : ""} · Updates 21:00 UTC</p>}

              {/* Yesterday's call vs what actually happened — builds trust in the desk read */}
              {d.scorecard && (
                (() => {
                  const v = d.scorecard.verdict;
                  const vc = v === "hit"
                    ? { dot: "bg-pos", text: "text-pos", ring: "border-pos/40", label: "Hit" }
                    : v === "miss"
                    ? { dot: "bg-neg", text: "text-neg", ring: "border-neg/40", label: "Miss" }
                    : { dot: "bg-amber-500", text: "text-amber-term", ring: "border-amber-500/40", label: "Partial" };
                  return (
                    <div
                      className={`mt-4 flex items-center gap-3 border ${vc.ring} bg-[#09090B] px-3 py-2`}
                      data-testid="dxy-scorecard"
                      title="Prior session dollar call vs the actual DXY move"
                    >
                      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 shrink-0">
                        Yesterday{d.scorecard.prev_date ? ` · ${d.scorecard.prev_date}` : ""}
                      </span>
                      <span className="h-3 w-px bg-zinc-800 shrink-0" />
                      <span className="text-[12px] text-zinc-300 leading-snug flex-1 min-w-0">{d.scorecard.line}</span>
                      {d.scorecard.accuracy && (
                        <span
                          className="shrink-0 hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-l border-zinc-800 pl-3"
                          data-testid="dxy-scorecard-accuracy"
                          title={`Desk hit rate over the last ${d.scorecard.accuracy.total} session${d.scorecard.accuracy.total === 1 ? "" : "s"}`}
                        >
                          {d.scorecard.accuracy.total}D Hit Rate
                          <span className="text-zinc-300 tabular-nums">{d.scorecard.accuracy.hits}/{d.scorecard.accuracy.total}</span>
                          <span className={`tabular-nums ${d.scorecard.accuracy.pct >= 50 ? "text-pos" : "text-neg"}`}>{d.scorecard.accuracy.pct}%</span>
                        </span>
                      )}
                      <span className={`shrink-0 inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] ${vc.text}`} data-testid="dxy-scorecard-verdict">
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${vc.dot}`} /> {vc.label}
                      </span>
                    </div>
                  );
                })()
              )}

              {/* DXY tape — hairline strip matching cross-asset cards */}
              {d.dxy_tape && (
                <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-zinc-900/60 border border-zinc-900/60 mt-5" data-testid="dxy-tape">
                  <TapeItem label="DXY Close" value={d.dxy_tape.close} />
                  <TapeItem label="Session Δ" value={d.dxy_tape.change} biasColor={d.bias} />
                  <TapeItem label="Tape Read" value={d.dxy_tape.read} small />
                </div>
              )}

              {/* DXY 15-session close trend — always live-sourced */}
              <DxyCloseHistory />
            </section>

            {/* ══ SESSION RECAP ══ */}
            {d.quick_answer?.length > 0 && (
              <section data-testid="dxy-quick" className="space-y-4">
                <ZoneHeader n="01" label="Session Recap" />
                <ul className="space-y-3">
                  {d.quick_answer.map((x, i) => (
                    <li key={i} className="text-[13px] text-zinc-300 leading-relaxed flex gap-2.5">
                      <span className="text-amber-term font-mono shrink-0">›</span> {x}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* ══ CROSS-ASSET DASHBOARD (white card) ══ */}
            {d.cross_asset?.length > 0 && (
              <Card n="02" label="Cross-Asset Impact" hint="closing prices" testid="dxy-cross-asset">
                <div className="grid grid-cols-2 md:grid-cols-4">
                  {d.cross_asset.map((c, i) => {
                    const sig = SIGNAL[c.signal] || SIGNAL.flat;
                    return (
                      <div key={i} className="p-4 flex flex-col gap-1.5 border-b border-r border-zinc-900/60" data-testid={`cross-asset-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{c.asset}</span>
                          <span className={`font-mono text-[11px] ${sig.cls}`}>{sig.arrow}</span>
                        </div>
                        {c.value && <span className={`font-mono text-lg tabular-nums ${sig.cls}`}>{c.value}</span>}
                        <span className="text-[12px] text-zinc-400 leading-snug">{c.read}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* ══ ASSET-BY-ASSET PAIRS ══ */}
            {d.pairs?.length > 0 && (
              <section data-testid="dxy-pairs" className="space-y-4">
                <ZoneHeader n="03" label="Asset by Asset" />
                <div className="divide-y divide-zinc-900/60 border-t border-zinc-900">
                  {d.pairs.map((p, i) => {
                    const pb = BIAS[p.bias] || BIAS.neutral;
                    return (
                      <div key={i} id={`dxy-pair-${canonicalPair(p.pair)}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-5 py-5 scroll-mt-28 rounded-sm" data-testid={`pair-${i}`}>
                        <div className="md:col-span-3 flex md:flex-col items-center md:items-start gap-3 md:gap-2">
                          <span className="font-mono text-base text-zinc-100">{p.pair}</span>
                          <span className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest ${pb.cls}`}>
                            <pb.Icon size={12} strokeWidth={1.5} /> {p.bias}
                          </span>
                        </div>
                        <div className="md:col-span-6"><Prose text={p.read} /></div>
                        <div className="md:col-span-3 space-y-3 md:border-l md:border-zinc-900/60 md:pl-5">
                          {p.levels && (
                            <div className="flex md:flex-col gap-1.5 md:gap-0.5">
                              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 shrink-0">Levels</div>
                              <div className="text-[12px] font-mono text-zinc-300">{p.levels}</div>
                            </div>
                          )}
                          {p.trigger && (
                            <div className="flex md:flex-col gap-1.5 md:gap-0.5">
                              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 shrink-0">Trigger</div>
                              <div className="text-[12px] text-zinc-400">{p.trigger}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ══ MACRO / GEOPOLITICS NARRATIVE (full width, breathing) ══ */}
            {d.sections?.length > 0 && (
              <section data-testid="dxy-narrative" className="space-y-4">
                <ZoneHeader n="04" label="Macro & Geopolitics" />
                <div className="space-y-8">
                  {d.sections.map((s, i) => (
                    <div key={i} data-testid={`dxy-section-${i}`} className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-6">
                      <h3 className="text-sm font-semibold text-amber-term md:col-span-1 leading-snug">{s.heading}</h3>
                      <div className="md:col-span-3"><Prose text={s.body} /></div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ══ KEY LEVELS (white card) ══ */}
            {d.levels?.length > 0 && (
              <Card n="05" label="Key Levels" testid="dxy-levels">
                <div className="divide-y divide-zinc-900/60">
                  {d.levels.map((l, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
                      <span className="font-mono text-[13px] text-amber-term shrink-0">{l.name}</span>
                      <span className="text-[12px] text-zinc-400 text-right leading-snug">{l.note}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* ══ SCENARIOS (white card, hairline rows) ══ */}
            {d.scenarios?.length > 0 && (
              <Card n="06" label="Scenarios" testid="dxy-scenarios">
                <div className="divide-y divide-zinc-900/60">
                  {d.scenarios.map((s, i) => (
                    <div key={i} className="px-4 py-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[13px] font-semibold text-zinc-100">{s.name}</span>
                        {s.weight && <span className="font-mono text-[12px] text-amber-term tabular-nums ml-auto">{s.weight}</span>}
                      </div>
                      <p className="text-[12px] text-zinc-400 leading-relaxed">{s.body}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* ══ INVALIDATION ══ */}
            {d.invalidation?.length > 0 && (
              <section data-testid="dxy-invalidation" className="space-y-4">
                <ZoneHeader n="07" label="Invalidation Triggers" />
                <div className="divide-y divide-zinc-900/60 border-t border-zinc-900">
                  {d.invalidation.map((iv, i) => (
                    <div key={i} className="flex gap-3 py-4" data-testid={`invalidation-${i}`}>
                      <AlertTriangle size={14} strokeWidth={1.75} className="text-neg shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-[13px] text-zinc-200 leading-snug">{iv.trigger}</p>
                        <p className="text-[12px] font-mono text-neg flex items-start gap-1.5"><ArrowRight size={11} className="mt-0.5 shrink-0" /> {iv.consequence}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ══ WHAT'S NEXT ══ */}
            {d.whats_next && (
              <section data-testid="dxy-next" className="space-y-4">
                <ZoneHeader n="08" label="What's Next · 24-48h" />
                <WhatsNext data={d.whats_next} />
              </section>
            )}

            {/* ══ TAKEAWAY ══ */}
            {d.takeaway && (
              <section className="border-l-2 border-amber-500 pl-4 py-1" data-testid="dxy-takeaway">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">Desk Takeaway</div>
                <p className="text-base sm:text-lg font-semibold tracking-tight text-zinc-100 leading-snug max-w-3xl">{d.takeaway}</p>
              </section>
            )}

            <p className="text-[11px] uppercase tracking-widest text-zinc-600 border-t border-zinc-900 pt-5">Educational analysis, not financial advice · Auto-generated from the terminal's live macro, rate, VIX & COT feeds</p>
          </div>
        )}
      </main>
    </div>
  );
}

function WhatsNext({ data }) {
  if (typeof data === "string") {
    return <p className="text-[13px] text-zinc-300 leading-loose max-w-3xl">{data}</p>;
  }
  const jump = (pair) => {
    const el = document.getElementById(`dxy-pair-${canonicalPair(pair)}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("pair-flash");
    setTimeout(() => el.classList.remove("pair-flash"), 1200);
  };
  return (
    <div className="space-y-7">
      {data.dollar && <div data-testid="whatsnext-dollar"><Prose text={data.dollar} cls="text-[13px] text-zinc-200 leading-relaxed" /></div>}

      {(data.catalysts?.length > 0 || data.pairs?.length > 0) && (
        <div className="border-l border-zinc-800 pl-5 space-y-9">
          {data.catalysts?.length > 0 && (
            <div className="space-y-2.5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-600">Upcoming Catalysts</div>
              <ul className="space-y-1.5">
                {data.catalysts.map((c, i) => (
                  <li key={i} className="text-[12px] text-zinc-300 leading-relaxed flex gap-2.5">
                    <span className="text-amber-term font-mono shrink-0">→</span> {c}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.pairs?.length > 0 && (
            <div className="space-y-2.5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-600">Pair Outlook · Next 24-48h</div>
              <div className="divide-y divide-zinc-900/60 border-t border-zinc-900">
                {data.pairs.map((p, i) => {
                  const pb = BIAS[p.bias] || BIAS.neutral;
                  return (
                    <button
                      key={i}
                      onClick={() => jump(p.pair)}
                      className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-1.5 md:gap-4 py-2.5 group"
                      data-testid={`whatsnext-pair-${i}`}
                    >
                      <div className="md:col-span-3 flex items-center gap-2">
                        <span className="font-mono text-[13px] text-zinc-200 group-hover:text-amber-term transition-colors">{p.pair}</span>
                        <span className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${pb.cls}`}>
                          <pb.Icon size={10} strokeWidth={2} /> {p.bias}
                        </span>
                      </div>
                      <p className="md:col-span-9 text-[12px] text-zinc-400 leading-relaxed">{p.expectation}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TapeItem({ label, value, small, biasColor }) {
  const cls = biasColor === "bullish" ? "text-pos" : biasColor === "bearish" ? "text-neg" : "text-zinc-100";
  return (
    <div className="flex-1 p-3 sm:p-4 flex flex-col gap-1.5" data-testid={`dxy-tape-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
      <span className={`font-mono ${small ? "text-[13px] leading-snug text-zinc-300" : `text-xl tabular-nums ${cls}`}`}>{value || "n/a"}</span>
    </div>
  );
}
